import {
  types,
  getSnapshot,
  getParent,
  type SnapshotIn,
} from 'mobx-state-tree';
import Transition from '../metamodel/transition';
import { modelReference } from '../refHelper';
import type { ITransition } from '../metamodel/transition';
import type { IFSMNode } from './fsmnode';
import type { IFSMView } from './fsm';

/**
 * A vertex for a link. It has x and y coordinates.
 */
const Vertex = types.model('Vertex', {
  x: types.number,
  y: types.number,
});

type VertexSnap = SnapshotIn<typeof Vertex>;

const LabelPosition = types.model({
  offset: types.number,
  distance: types.maybe(types.number),
});

type LabelPositionSnap = SnapshotIn<typeof LabelPosition>;

/**
 * The FSMLink instance interface.
 *
 * HAND-DECLARED because FSMLink sits on the FSM recursion boundary: it is held in
 * `IFSMView.links` AND reaches `IFSMView` back through `get sourceNode()` /
 * `get targetNode()` (which call `getParent(self, 2)`). Typing those against the
 * interfaces cuts the cycle instead of re-inferring `typeof FSMLink`.
 */
export interface IFSMLink {
  readonly transition: ITransition;
  readonly vertices: { x: number; y: number }[];
  readonly labelPosition: { offset: number; distance: number | undefined } | undefined;
  readonly jsVertices: ReadonlyArray<{ readonly x: number; readonly y: number }>;
  readonly sourceNode: IFSMNode | undefined;
  readonly targetNode: IFSMNode | undefined;
  fixVertices(): void;
  moveVertices(moveX: number, moveY: number): void;
  setVertices(vertices: VertexSnap[]): void;
  clearVertices(): void;
  setLabelPosition(position: LabelPositionSnap | undefined): void;
}

/**
 * A link in an FSM. This is the visual counterpart of a transition. It has a
 * list of vertices.
 */
const FSMLink = types
  .model('FSMLink', {
    transition: modelReference<typeof Transition>(Transition),
    vertices: types.optional(types.array(Vertex), []),
    labelPosition: types.maybe(LabelPosition),
  })
  .views((self0) => {
    const self = self0 as unknown as IFSMLink;
    return {
      // self.vertices is a MobX Observable Array, rappid/joint.js expects a
      // regular array
      get jsVertices() {
        return getSnapshot(self0.vertices);
      },
      get sourceNode(): IFSMNode | undefined {
        const uifsm: IFSMView = getParent(self0, 2);
        const sourceStateId = self.transition.sourceState.id;
        return uifsm.findNodeById(sourceStateId);
      },
      get targetNode(): IFSMNode | undefined {
        const uifsm: IFSMView = getParent(self0, 2);
        const targetStateId = self.transition.targetState.id;
        return uifsm.findNodeById(targetStateId);
      },
    };
  })
  .actions((self0) => {
    const self = self0 as unknown as IFSMLink;
    return {
      fixVertices() {
        // Make sure there are at least 2 vertices for self-links, because it looks very strange otherwise
        if (self.transition.sourceState === self.transition.targetState && self.vertices.length < 2) {
          if (self.vertices.length === 0) {
            const { position, size } = self.sourceNode!;
            const nodeBottomCenter = {
              x: position.x + size.width / 2,
              y: position.y + size.height,
            };
            self0.vertices.replace([
              {
                x: nodeBottomCenter.x - 20,
                y: nodeBottomCenter.y + 30,
              },
              {
                x: nodeBottomCenter.x + 20,
                y: nodeBottomCenter.y + 30,
              },
            ]);
          } else {
            // < 2 and not 0 -> 1
            const first = self0.vertices[0];
            self0.vertices.push({
              x: first.x + 40,
              y: first.y,
            });
          }
        }
      },
      moveVertices(moveX: number, moveY: number) {
        for (const v of self0.vertices) {
          v.x = v.x + moveX;
          v.y = v.y + moveY;
        }
      },
      setVertices(vertices: VertexSnap[]) {
        self0.vertices = vertices as unknown as typeof self0.vertices;
        self.fixVertices();
      },
      clearVertices() {
        self0.vertices = [] as unknown as typeof self0.vertices;
        self.fixVertices();
      },
      setLabelPosition(position: LabelPositionSnap | undefined) {
        self0.labelPosition = position as unknown as typeof self0.labelPosition;
      },
      afterAttach() {
        // afterAttach iso afterCreate because we need the parent!
        self.fixVertices();
      },
    };
  });

export default FSMLink;
